import { Component, OnInit, ElementRef, ViewChild, Input } from "@angular/core";
import { FormControl } from "@angular/forms";
import { IonInput, IonicModule, ModalController } from "@ionic/angular";
import { Observable, of } from "rxjs";
import {
  debounceTime,
  filter,
  map,
  startWith,
  switchMap,
} from "rxjs/operators";
import { AppSettings } from "../../../shared/app-settings";
import { CustomerData } from "../../../shared/customer-data";
import { AddressData } from "../../../shared/address-data";
import { HttpClient } from "@angular/common/http";
import { IonicSelectableComponent } from "ionic-selectable";
import { Storage } from "@ionic/storage-angular";
import { CustomerNorwegianData } from "src/app/shared/customer-norwegian-data";

@Component({
  selector: "app-customer-search",
  templateUrl: "./customer-search.component.html",
  styleUrl: "./customer-search.component.css",
  standalone: false,
})
export class CustomerSearchComponent {
  @Input() query: string = "";
  @Input() editMode: boolean = false;
  /** Flervalg: avkrysning, valg beholdes på tvers av søk, returnerer { customers } */
  @Input() multiSelect = false;
  /** Kundenr som allerede er tildelt - vises låst i flervalg */
  @Input() excludeCustomerNos: number[] = [];

  selectedCustomers = new Map<number, any>();

  customerCtrl = new FormControl();
  filteredCustomers: Observable<any[]>;
  selectedCustomer: any = null;
  DisableBREG = "0";
  RestrictedCustomerAccess = false;
  private customerDataService: CustomerData;

  @ViewChild("customerSearch", { static: false }) customerSearch: IonInput;
  @ViewChild("customerSearchRef", { static: false })
  customerSearchRef: ElementRef;

  constructor(
    private http: HttpClient,
    private modalCtrl: ModalController,
    private storage: Storage
  ) {
    this.customerDataService = new CustomerData(
      this.http,
      `${AppSettings.API_ENDPOINT}/User/CustomerSearch/?query=`
    );
  }

  ngOnInit() {
    this.storage
      .get(AppSettings.StorageKeys.DisableBREG)
      .then((data) => (this.DisableBREG = data));
    // Begrensede brukere skal ikke få BRREG-forslag - de skal kun se tildelte kunder
    this.storage
      .get(AppSettings.StorageKeys.RestrictedCustomerAccess)
      .then((data) => (this.RestrictedCustomerAccess = data === true));
    this.customerCtrl.setValue(this.query);

    this.filteredCustomers = this.customerCtrl.valueChanges.pipe(
      startWith(this.query),
      debounceTime(300),
      switchMap((name) => this.searchCustomerNorwegianRegistry(name))
    );
  }

  ngAfterViewInit() {
    // Set focus on the input field after the view has been initialized
    setTimeout(() => this.customerSearch.setFocus(), 1000);

    // Trigger search on every keyup event
  }

  private _filterCustomers(value: string): Observable<any[]> {
    return this.customerDataService.search(value);
  }

  private searchCustomerNorwegianRegistry(query: string): Observable<any[]> {
    return new Observable((observer) => {
      this._filterCustomers(query).subscribe((customers) => {
        if (
          customers.length === 0 &&
          query.length >= 3 &&
          this.DisableBREG === "0" &&
          !this.RestrictedCustomerAccess &&
          !this.multiSelect
        ) {
          const norwegianDataService = new CustomerNorwegianData(
            this.http,
            `${AppSettings.NorwegianRegistryAPI}${query}`
          );
          norwegianDataService.search().subscribe((norwegianCustomers) => {
            observer.next(norwegianCustomers);
            observer.complete();
          });
        } else {
          observer.next(customers);
          observer.complete();
        }

        if (this.editMode && !this.multiSelect && customers.length == 1) {
          this.selectCustomer(customers[0]);
        }
      });
    });
  }

  async clearSearch() {
    this.customerCtrl.setValue("");
    this.customerSearch.setFocus();
  }

  async closeModal() {
    await this.modalCtrl.dismiss();
  }

  async selectCustomer(customer) {
    if (this.multiSelect) {
      this.toggleCustomer(customer);
      return;
    }
    await this.modalCtrl.dismiss({ customer });
  }

  isSelectable(customer): boolean {
    return (
      customer?.CustomerNo > 0 &&
      !this.excludeCustomerNos.includes(customer.CustomerNo)
    );
  }

  isAlreadyAdded(customer): boolean {
    return this.excludeCustomerNos.includes(customer?.CustomerNo);
  }

  private toggleCustomer(customer) {
    if (!this.isSelectable(customer)) {
      return;
    }
    if (this.selectedCustomers.has(customer.CustomerNo)) {
      this.selectedCustomers.delete(customer.CustomerNo);
    } else {
      this.selectedCustomers.set(customer.CustomerNo, customer);
    }
  }

  unselect(customerNo: number) {
    this.selectedCustomers.delete(customerNo);
  }

  async confirmSelection() {
    if (this.selectedCustomers.size === 0) {
      return;
    }
    await this.modalCtrl.dismiss({
      customers: Array.from(this.selectedCustomers.values()),
    });
  }
}
